import { useGetSuppliersAndSourcesQuery } from '@/__generated__/graphql';
import type { Message } from '@/common/db-typebox';
import { Alert, message } from 'antd';
import { useLiveQuery } from 'dexie-react-hooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { useTreatyClient } from '../../lib/client';
import { db } from '../../lib/db';
import { useAuth } from '../../stores/AuthContext';
import { MessageInput } from './components/MessageInput';
import { MessageList } from './components/MessageList';
import {
  type SearchFilter,
  SearchFilterComponent,
} from './components/SearchFilter';
import { ConversationList } from './components/conversation-list';
import { useSyncData } from './hooks/useSyncData';

export const LiveChatPage = observer(() => {
  const { user } = useAuth();
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [processingConversations, setProcessingConversations] = useState<
    Set<string>
  >(new Set());
  const client = useTreatyClient();
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const { loading: syncLoading, syncing } = useSyncData(selectedConversationId);
  const [filter, setFilter] = useState<SearchFilter>({
    selectedSuppliers: [],
    sources: [],
    allow_null_reviews: false,
    include_no_price: false,
    price_range: { min: undefined, max: undefined },
    suppliers: [],
    limit: 10,
  });

  // Query suppliers and their sources
  const { data: supplierData } = useGetSuppliersAndSourcesQuery();

  const suppliers =
    supplierData?.suppliers.filter(
      (supplier) =>
        supplier.id !== '66ac22cb-0332-4c04-b2fc-a6517692fc8c' ||
        (supplier.id === '66ac22cb-0332-4c04-b2fc-a6517692fc8c' &&
          [
            'yannickcrespo@gmail.com',
            'jessehysum@gmail.com',
            'pablo@soul.wine',
            'test@keyp.dev',
            'atcha.979@gmail.com',
          ].includes(user?.email ?? '')),
    ) ?? [];

  // Get current conversation messages
  const messages =
    useLiveQuery<Message[]>(async () => {
      if (!selectedConversationId || !user) return [];

      // First get all messages sorted by created_at
      const allMessages = await db.messages
        .where('conversation_id')
        .equals(selectedConversationId)
        .sortBy('created_at');

      // Group messages by parent_id
      const messageGroups: Message[] = [];
      const messageMap = new Map<string, Message>();

      // First pass: Create map of all messages
      allMessages.forEach((msg) => {
        messageMap.set(msg.id, msg);
      });

      // Second pass: Organize messages in sequence
      allMessages.forEach((msg) => {
        // If this is a user message or a message without parent, add it directly
        if (msg.content.role === 'user' || !msg.parent_id) {
          messageGroups.push(msg);
        } else {
          // For assistant messages, find their parent and add them right after
          const parent = messageMap.get(msg.parent_id);
          if (parent) {
            const parentIndex = messageGroups.findIndex(
              (m) => m.id === parent.content.id,
            );
            if (parentIndex !== -1) {
              messageGroups.splice(parentIndex + 1, 0, msg);
            } else {
              // If parent not found, just append to the end
              messageGroups.push(msg);
            }
          }
        }
      });

      return messageGroups;
    }, [selectedConversationId, user]) ?? [];

  // Send message
  const sendMessage = useCallback(async () => {
    if (!input.trim() || !selectedConversationId) return;
    if (processingConversations.has(selectedConversationId)) return;

    try {
      setProcessingConversations(
        (prev) => new Set([...prev, selectedConversationId]),
      );
      setError(null); // Reset error state before sending

      // Process filter sources to handle both supplier-only and supplier-with-source selections
      const processedFilter = {
        ...filter,
        // suppliers: filter.selectedSuppliers
        //   .map((path) => {
        //     if (path.length > 0 && path[0]?.startsWith('supplier:')) {
        //       return path[0].replace('supplier:', '');
        //     }
        //     return null;
        //   })
        //   .filter((value): value is string => value !== null),
        sources: filter.selectedSuppliers.flatMap((path) => {
          // If path has source (length === 2), use the source
          if (path.length === 2 && path[1]?.startsWith('source:')) {
            return [path[1].replace('source:', '')];
          }
          // If path only has supplier, find all its sources
          if (path.length === 1 && path[0]?.startsWith('supplier:')) {
            const supplierId = path[0].replace('supplier:', '');
            const supplier = suppliers.find((s) => s.id === supplierId);
            return supplier?.sources.map((source) => source.id) || [];
          }
          return [];
        }),
      };

      await client.api.chat
        .message({
          conversationId: selectedConversationId,
        })
        .send.post({
          dish_description: input.trim(),
          filter: {
            ...processedFilter,
            limit: processedFilter.limit ?? 10,
          },
        });

      setInput('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      message.error({
        content: errorMessage,
        key: 'send-message-error',
      });
      console.error('Failed to send message:', err);
    } finally {
      setProcessingConversations((prev) => {
        const next = new Set(prev);
        next.delete(selectedConversationId);
        return next;
      });
    }
  }, [
    input,
    selectedConversationId,
    processingConversations,
    client,
    filter,
    suppliers,
  ]);

  return (
    <div className="flex p-8 h-full gap-4">
      {/* Conversation list sidebar */}
      <div className="w-80 p-4 border rounded-lg bg-background">
        <ConversationList
          selectedId={selectedConversationId}
          onSelect={setSelectedConversationId}
        />
      </div>

      {/* Message area */}
      <div className="flex-1 p-4 border rounded-lg bg-background">
        <div className="flex flex-col h-full overflow-hidden">
          {selectedConversationId ? (
            <>
              {error && (
                <Alert type="error" message={error} className="mb-4" showIcon />
              )}
              <div className="flex-1 overflow-y-auto min-h-0">
                <MessageList
                  messages={messages}
                  isLoading={syncLoading || syncing}
                  onError={setError}
                />
              </div>
              <div className="flex flex-col gap-4 mt-4 flex-shrink-0">
                <SearchFilterComponent
                  suppliers={suppliers}
                  filter={filter}
                  onChange={setFilter}
                />
                <MessageInput
                  input={input}
                  isProcessing={
                    selectedConversationId
                      ? processingConversations.has(selectedConversationId)
                      : false
                  }
                  disabled={filter.selectedSuppliers.length === 0}
                  placeholder={
                    filter.selectedSuppliers.length === 0
                      ? 'Select sources first...'
                      : undefined
                  }
                  onChange={setInput}
                  onSend={sendMessage}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              Select a conversation or create a new one
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
